import { LoadingOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import React from 'react';
import { Button, Col, Form, FormInstance, Input, PageTitle, Row, Table, Typography } from 'components';
import { FilterIcon, PlusIcon, ViewIcon } from 'components/common/icons';
import { useItems } from './useItems';
import styles from 'pages/admin/clients/styles.module.scss';
import { Filters } from './filters';
import { HasAccess } from 'components/common/accessControl';
import { PERMISSIONS } from 'constants/permissions';
import { AddItemModal } from 'pages/admin/items/addItem';
import { SuppliersDrawer } from 'pages/admin/items/suppliersDrawer';

const { Search } = Input;
const { Text } = Typography;

type EntityTableType = {
  name: string;
  id: string;
};

export interface ItemDataType {
  id: string;
  uuid: string | number;
  itemSKU: string;
  description: string;
  brand: EntityTableType;
  supplier: EntityTableType;
  averagePrice: number;
  createDate: string;
  lastModifiedDate: string;
}

export const Items: React.FC = () => {
  const [filtersForm] = Form.useForm<FormInstance>();
  const {
    addModalState,
    onAddModalStateChange,
    onCloseFilters,
    onOpenFilters,
    openFilters,
    handleTableChange,
    routeChange,
    isLoading,
    pagination,
    onSearch,
    data,
    total,
    applyFilters,
    onRedirectToSupplier,
    onRedirectToBrand,
    resetFilters,
    drawerState,
    onExpand,
    suppliersData,
  } = useItems();

  const columns: ColumnsType<ItemDataType> = [
    {
      title: 'Item Number',
      dataIndex: 'id',
      key: 'id',
      render: (_, { uuid }) => {
        return uuid;
      },
    },
    {
      title: 'SKU',
      dataIndex: 'itemSKU',
      key: 'itemSKU',
      width: 150,
      ellipsis: true,
      render: (value) => {
        return (
          <Text style={{ maxWidth: '150px' }} ellipsis={{ tooltip: true }}>
            {value}
          </Text>
        );
      },
    },
    {
      title: 'Item Description',
      dataIndex: 'description',
      key: 'description',
      sorter: true,
      width: 200,
      ellipsis: true,
      render: (value) => {
        return (
          <Text style={{ maxWidth: '230px' }} ellipsis={{ tooltip: true }}>
            {value}
          </Text>
        );
      },
    },
    {
      title: 'Brand',
      dataIndex: 'brand',
      key: 'brand',
      width: 130,
      ellipsis: true,
      render: (value) => {
        return (
          <Button
            size={'small'}
            type={'text'}
            variant={'text'}
            className={'underline-button'}
            onClick={() => onRedirectToBrand(value.id)}
          >
            <Text underline type={'secondary'} ellipsis={{ tooltip: true }}>
              {value?.name}
            </Text>
          </Button>
        );
      },
    },
    {
      title: 'Suppliers',
      dataIndex: 'suppliers',
      key: 'suppliers',
      width: 200, // Set max width
      render: (suppliers) => {
        return (
          <Row align={'middle'} wrap={false} justify={'space-between'}>
            <Row className={styles.users_column} wrap={false}>
              {!!suppliers?.length && (
                <Button
                  size={'small'}
                  type={'text'}
                  variant={'text'}
                  className={'underline-button'}
                  onClick={() => onRedirectToSupplier(suppliers[0].id)}
                >
                  <Text underline type={'secondary'} ellipsis={{ tooltip: true }}>
                    {suppliers[0].name}
                  </Text>
                </Button>
              )}
            </Row>
            {suppliers?.length > 1 && (
              <Button
                size={'small'}
                type={'text'}
                variant={'text'}
                className={'underline-button'}
                onClick={() => onExpand(suppliers)}
              >
                <Text underline type={'secondary'}>
                  +{suppliers?.length - 1}
                </Text>
              </Button>
            )}
          </Row>
        );
      },
    },
    {
      title: 'Average Price',
      dataIndex: 'averagePrice',
      key: 'averagePrice',
      minWidth: 130,
    },
    {
      title: 'Created Date',
      dataIndex: 'formattedCreatedDate',
      key: 'createDate',
      render: (value) => value?.day,
      minWidth: 160,
      sorter: true,
    },
    {
      title: 'Last Updated',
      dataIndex: 'formattedLastModifiedDate',
      key: 'lastModifiedDate',
      render: (value) => value?.day,
      minWidth: 150,
      sorter: true,
    },
    {
      fixed: 'right',
      width: '36px',
      key: 'actions',
      render: (value) => (
        <Button onClick={() => routeChange(value.id)} type={'text'} size={'small'} icon={<ViewIcon />} />
      ),
    },
  ];

  return (
    <Col span={24} className={styles.main}>
      <Row gutter={[0, 24]}>
        <Col span={24}>
          <Row align={'middle'} justify={'space-between'}>
            <PageTitle title={'Items'} />
            <HasAccess role={PERMISSIONS.items.item.write}>
              <Button
                type={'primary'}
                onClick={() => onAddModalStateChange(true)}
                icon={<PlusIcon color={'#FFFFFF'} />}
              >
                Add New
              </Button>
            </HasAccess>
          </Row>
        </Col>
        <Col span={24} className={styles.main_container}>
          <Row>
            <Col span={24}>
              <Row justify={'end'} className={styles.actions_container}>
                <Col style={{ width: '300px' }}>
                  <Search enterButton={false} placeholder={'Search'} onChange={(e) => onSearch(e.target.value)} />
                </Col>
                <Button color="default" variant="filled" onClick={onOpenFilters} icon={<FilterIcon />} />
              </Row>
            </Col>
            <Col span={24} style={{ paddingTop: '20px' }}>
              <HasAccess role={PERMISSIONS.items.item.read}>
                <Table
                  tableLayout={'auto'}
                  bordered
                  scroll={{
                    x: true,
                  }}
                  rowKey="id"
                  size={'small'}
                  dataSource={data}
                  columns={columns}
                  loading={{
                    indicator: <LoadingOutlined style={{ fontSize: 48 }} spin />,
                    spinning: isLoading,
                  }}
                  locale={
                    isLoading
                      ? { emptyText: null } // Hide "No Data" text while loading
                      : {} // Use default AntD "No Data" icon when empty
                  }
                  onChange={handleTableChange}
                  pagination={{
                    position: ['bottomCenter'],
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} items`,
                    size: 'default',
                    current: pagination.page,
                    pageSize: pagination.size,
                    total: total,
                  }}
                />
              </HasAccess>
            </Col>
          </Row>
        </Col>
      </Row>
      <Filters
        form={filtersForm}
        drawerOpen={openFilters}
        closeDrawer={onCloseFilters}
        applyFilters={applyFilters}
        resetFilters={resetFilters}
      />
      <AddItemModal modalState={addModalState} onModalStateChange={onAddModalStateChange} />
      <SuppliersDrawer onExpand={onExpand} drawerState={drawerState} data={suppliersData} />
    </Col>
  );
};
